<template>
    <div class="cp-page">
        <!-- Header -->
        <div class="cp-header">
            <div class="cp-header-left">
                <div class="cp-icon-wrap"><i class="fas fa-file-invoice-dollar"></i></div>
                <div>
                    <h2 class="cp-title">Create New Payroll</h2>
                    <span class="cp-subtitle">{{ initialized ? 'Step 2 of 2 — Review & Generate' : 'Step 1 of 2 — Select Period' }}</span>
                </div>
            </div>
            <div class="cp-steps">
                <div :class="['cp-step', { active: !initialized, done: initialized }]">
                    <span class="cp-step-num">{{ initialized ? '✓' : '1' }}</span> Setup
                </div>
                <div class="cp-step-line" :class="{ filled: initialized }"></div>
                <div :class="['cp-step', { active: initialized }]">
                    <span class="cp-step-num">2</span> Review
                </div>
            </div>
        </div>

        <!-- Step 1: Configuration -->
        <div v-if="!initialized" class="cp-step1">
            <form @submit.prevent="fetchEmployeesAndSalaryDetails" class="cp-form-card">
                <h3 class="cp-form-title"><i class="fas fa-cog"></i> Payroll Configuration</h3>
                <div class="cp-form-grid">
                    <div class="cp-field">
                        <label class="cp-label">Year <span class="cp-req">*</span></label>
                        <SelectDropdown
                            v-if="yearOptions.length"
                            :id="`form-year`"
                            :options="yearOptions"
                            :value="selectedYear"
                            @change="selectedYear = $event"
                        />
                    </div>
                    <div class="cp-field">
                        <label class="cp-label">Month <span class="cp-req">*</span></label>
                        <SelectDropdown
                            v-if="monthOptions.length"
                            :id="`form-month`"
                            :options="monthOptions"
                            :value="selectedMonth"
                            @change="selectedMonth = $event"
                        />
                    </div>
                    <div class="cp-field cp-field-full">
                        <label class="cp-label">Company <span class="cp-req">*</span></label>
                        <SelectDropdown
                            :id="`form-company`"
                            v-if="companies.length"
                            :options="companies.map((item) => ({ id: item.id, title: item.name }))"
                            :value="selectedCompany"
                            @change="selectedCompany = $event"
                        />
                    </div>
                    <div class="cp-field cp-field-full">
                        <label class="cp-label">Departments <span class="cp-req" v-if="!selectAllDepartments">*</span></label>
                        <SelectDropdown
                            :id="`form-departments`"
                            v-if="departments.length"
                            :multiple="true"
                            :options="departments.map((item) => ({ id: item.id, title: item.name }))"
                            :value="selectedDepartments"
                            @change="selectedDepartments = $event"
                        />
                        <label class="cp-checkbox-label">
                            <input v-model="selectAllDepartments" type="checkbox" class="cp-checkbox" />
                            <span>Select all (including employees without departments)</span>
                        </label>
                    </div>
                </div>
                <button type="submit" class="cp-next-btn" :disabled="fetchingEmployees">
                    <i :class="fetchingEmployees ? 'fas fa-spinner fa-spin' : 'fas fa-arrow-right'"></i>
                    {{ fetchingEmployees ? 'Loading...' : 'Next — Select Employees' }}
                </button>
            </form>
        </div>

        <!-- Step 2: Review & Generate -->
        <div v-else class="cp-step2">
            <!-- Summary strip -->
            <div class="cp-summary-strip">
                <div class="cp-summary-card">
                    <div class="cp-summary-icon cp-si-blue"><i class="fas fa-building"></i></div>
                    <div>
                        <span class="cp-summary-label">Company</span>
                        <span class="cp-summary-value">{{ selectedCompanyName }}</span>
                    </div>
                </div>
                <div class="cp-summary-card">
                    <div class="cp-summary-icon cp-si-purple"><i class="fas fa-calendar-alt"></i></div>
                    <div>
                        <span class="cp-summary-label">Period</span>
                        <span class="cp-summary-value">{{ selectedMonthTitle }} {{ selectedYear }}</span>
                    </div>
                </div>
                <div class="cp-summary-card">
                    <div class="cp-summary-icon cp-si-teal"><i class="fas fa-users"></i></div>
                    <div>
                        <span class="cp-summary-label">Employees</span>
                        <span class="cp-summary-value">{{ employeePayrollData ? employeePayrollData.length : 0 }}</span>
                    </div>
                </div>
            </div>

            <!-- Action bar -->
            <div class="cp-action-bar">
                <div class="cp-action-left">
                    <button class="cp-back-btn" @click="initialized = false"><i class="fas fa-arrow-left"></i> Back</button>
                    <router-link target="_blank" class="cp-edit-link" :to="`/companies/${selectedCompany}?active_tab=PAYROLL`">
                        <i class="fas fa-external-link-alt"></i> Company Payroll Settings
                    </router-link>
                </div>
                <button class="cp-generate-btn" @click="generatePaySlips" :disabled="generatingPaySlips">
                    <i :class="generatingPaySlips ? 'fas fa-spinner fa-spin' : 'fas fa-file-invoice'"></i>
                    {{ generatingPaySlips ? 'Generating...' : 'Generate Pay Slips' }}
                </button>
            </div>

            <!-- Note -->
            <div class="cp-note">
                <i class="fas fa-info-circle"></i>
                Reload the page if company or employee payroll settings were changed after loading.
            </div>

            <!-- Payroll Form List -->
            <CreatePayrollFormList
                ref="payrollForm"
                :employeePayrollData="employeePayrollData"
            />

            <!-- Bottom generate button -->
            <div class="cp-bottom-bar">
                <button class="cp-generate-btn" @click="generatePaySlips" :disabled="generatingPaySlips">
                    <i :class="generatingPaySlips ? 'fas fa-spinner fa-spin' : 'fas fa-file-invoice'"></i>
                    {{ generatingPaySlips ? 'Generating...' : 'Generate Pay Slips' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import CreatePayrollFormList from "../../components/payroll/CreatePayrollFormList.vue"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"

export default {
    components: { SelectDropdown, CreatePayrollFormList },
    data() {
        return {
            initialized: false,
            startDate: null,
            endDate: null,
            companies: [],
            selectAllDepartments: false,
            departments: [],
            yearOptions: [],
            monthOptions: [
                { id: 1, title: "January" }, { id: 2, title: "February" }, { id: 3, title: "March" },
                { id: 4, title: "April" }, { id: 5, title: "May" }, { id: 6, title: "June" },
                { id: 7, title: "July" }, { id: 8, title: "August" }, { id: 9, title: "September" },
                { id: 10, title: "October" }, { id: 11, title: "November" }, { id: 12, title: "December" },
            ],
            selectedYear: new Date().getFullYear(),
            selectedMonth: new Date().getMonth() + 1,
            selectedCompany: null,
            selectedDepartments: null,
            fetchingEmployees: false,
            generatingPaySlips: false,
            payslipsGenerated: false,
            employeePayrollData: null,
        }
    },
    mounted() {
        this.initDates()
        this.fetchCompanies()
        this.fetchDepartments()
    },
    computed: {
        selectedCompanyName() {
            return this.companies.find((item) => item.id == this.selectedCompany)?.name
        },
        selectedMonthTitle() {
            return this.monthOptions.find((item) => item.id == this.selectedMonth)?.title
        },
    },
    methods: {
        updateDates() {
            this.startDate = new Date(this.selectedYear, this.selectedMonth - 1, 1)
            this.endDate = new Date(this.selectedYear, this.selectedMonth, 0)
        },
        initDates() {
            const currentYear = new Date().getFullYear()
            this.yearOptions = [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((item) => ({ id: item, title: item }))
            this.updateDates()
        },
        async fetchCompanies() {
            const res = await http.get("/companies?for_select=true")
            this.companies = res.data.result
        },
        async fetchDepartments() {
            const res = await http.get("/departments?for_select=true")
            this.departments = res.data.result
        },
        async fetchEmployeesAndSalaryDetails() {
            if (!this.selectedYear || !this.selectedMonth || !this.selectedCompany || (!this.selectAllDepartments && !this.selectedDepartments)) {
                return this.$toast.error("Please fill mandatory fields!")
            }
            this.fetchingEmployees = true
            try {
                const res = await http.get(
                    `/payroll-records/creation-data?company=${this.selectedCompany}&year=${this.selectedYear}&month=${Number(this.selectedMonth)}&department=${this.selectedDepartments}&select_all_departments=${this.selectAllDepartments ? 1 : 0}`
                )
                this.employeePayrollData = res.data.result
                this.initialized = true
            } catch (err) {
                console.log("error in fetching employee details", err)
                this.$toast.error(errMsg(err, "Could not fetch data!"))
            }
            this.fetchingEmployees = false
        },
        async generatePaySlips() {
            const payrollPaymentSlips = this.$refs.payrollForm.getData()
            if (!payrollPaymentSlips.length) return this.$toast.error("No data to generate payroll payment slip!")
            this.generatingPaySlips = true
            try {
                await http.post("/payroll-records", {
                    payrollData: { companyId: this.selectedCompany, startDate: this.startDate, endDate: this.endDate, month: this.selectedMonth, year: this.selectedYear },
                    payrollPaymentSlips,
                })
                this.$toast.success("Payroll generated!")
                this.payslipsGenerated = true
                this.$router.push("/payroll-history")
            } catch (err) {
                console.log("error in generating payroll", err)
                this.$toast.error(errMsg(err, "Could not create payroll!"))
            }
            this.generatingPaySlips = false
        },
    },
    watch: {
        selectedYear() { this.updateDates() },
        selectedMonth() { this.updateDates() },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.cp-page { animation: cpFade .3s ease; }
@keyframes cpFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.cp-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:14px; box-shadow:$shadowSm; flex-wrap:wrap; }
.cp-header-left { display:flex; align-items:center; gap:10px; }
.cp-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.cp-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.cp-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }

/* Steps indicator */
.cp-steps { display:flex; align-items:center; gap:0; }
.cp-step { display:flex; align-items:center; gap:5px; font-size:12px; font-weight:600; color:$gray400; padding:5px 10px; border-radius:$radiusFull; transition:all $transitionFast;
    &.active { color:$primaryColor; background:$primaryLight; }
    &.done { color:#16a34a; background:#dcfce7; }
}
.cp-step-num { width:20px; height:20px; border-radius:$radiusFull; background:$gray200; color:white; display:inline-flex; align-items:center; justify-content:center; font-size:10px; font-weight:700;
    .active & { background:$primaryColor; }
    .done & { background:#16a34a; }
}
.cp-step-line { width:32px; height:2px; background:$gray200; margin:0 4px; transition:background $transitionFast; &.filled { background:#16a34a; } }

/* Step 1 Form */
.cp-step1 { max-width:600px; }
.cp-form-card { background:white; border:1px solid $gray100; border-radius:10px; padding:20px; box-shadow:$shadowSm; }
.cp-form-title { font-size:13px; font-weight:700; color:$gray800; margin:0 0 16px; display:flex; align-items:center; gap:6px; i { font-size:12px; color:$primaryColor; } }
.cp-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:16px; }
.cp-field { display:flex; flex-direction:column; gap:4px; }
.cp-field-full { grid-column:1/-1; }
.cp-label { font-size:12px; font-weight:600; color:$gray600; }
.cp-req { color:#dc2626; }
.cp-checkbox-label { display:flex; align-items:center; gap:6px; font-size:12px; color:$gray500; font-weight:500; margin-top:6px; cursor:pointer; }
.cp-checkbox { accent-color:$primaryColor; }
.cp-next-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 20px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:13px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } &:disabled { opacity:0.6; cursor:not-allowed; } }

/* Step 2 */
.cp-summary-strip { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:12px; }
.cp-summary-card { display:flex; align-items:center; gap:10px; background:white; border:1px solid $gray100; border-radius:8px; padding:12px 14px; box-shadow:$shadowSm; }
.cp-summary-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.cp-si-blue { background:#eff6ff; color:#2563eb; }
.cp-si-purple { background:#f5f3ff; color:#7c3aed; }
.cp-si-teal { background:#f0fdfa; color:#0d9488; }
.cp-summary-label { font-size:10.5px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; display:block; }
.cp-summary-value { font-size:13.5px; font-weight:700; color:$gray800; }

/* Action bar */
.cp-action-bar { display:flex; align-items:center; justify-content:space-between; gap:10px; background:white; border:1px solid $gray100; border-radius:8px; padding:8px 14px; margin-bottom:10px; box-shadow:$shadowSm; flex-wrap:wrap; }
.cp-action-left { display:flex; align-items:center; gap:8px; }
.cp-back-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 12px; border:1px solid $gray200; border-radius:6px; background:white; color:$gray600; font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; i { font-size:10px; } &:hover { background:$gray50; border-color:$gray300; } }
.cp-edit-link { display:inline-flex; align-items:center; gap:5px; padding:7px 12px; border:1px solid $gray200; border-radius:6px; background:white; color:$primaryColor; font-size:12px; font-weight:600; text-decoration:none; transition:all $transitionFast; i { font-size:10px; } &:hover { background:$primaryLight; border-color:rgba(0,94,137,.15); } }
.cp-generate-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:6px; background:linear-gradient(135deg,#16a34a,#15803d); color:white; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; &:hover { background:linear-gradient(135deg,#15803d,#166534); } &:disabled { opacity:0.6; cursor:not-allowed; } }

/* Note */
.cp-note { display:flex; align-items:center; gap:8px; background:#fffbeb; border:1px solid #fde68a; border-radius:6px; padding:8px 14px; margin-bottom:12px; font-size:12px; color:#92400e; font-weight:500; i { font-size:12px; color:#d97706; flex-shrink:0; } }

/* Bottom bar */
.cp-bottom-bar { display:flex; justify-content:flex-end; padding-top:12px; border-top:1px solid $gray100; margin-top:12px; }

@media(max-width:640px) {
    .cp-form-grid { grid-template-columns:1fr; }
    .cp-field-full { grid-column:auto; }
    .cp-summary-strip { grid-template-columns:1fr; }
    .cp-header { flex-direction:column; align-items:stretch; }
    .cp-steps { justify-content:center; }
}
</style>
